import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface ErrorInfo {
  code: string;
  message: string;
  details?: unknown;
  userMessage: string;
  shouldShowToUser: boolean;
  requiresAuth?: boolean;
  requiresRetry?: boolean;
}

export interface ErrorHandlingStrategy {
  canHandle(error: HttpErrorResponse): boolean;

  handle(error: HttpErrorResponse): ErrorInfo;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  private translate = inject(TranslateService);

  private strategies: ErrorHandlingStrategy[] = [];

  constructor() {
    this.initializeStrategies();
  }

  /**
   * Error HTTP y devuelve info del error
   */
  handleError(error: HttpErrorResponse): ErrorInfo {
    const strategy = this.strategies.find((s) => s.canHandle(error));

    if (strategy) {
      return strategy.handle(error);
    }

    // Fallback para errores no manejados
    return this.createGenericErrorInfo(error);
  }

  handleErrorObservable(error: HttpErrorResponse): Observable<never> {
    const errorInfo = this.handleError(error);
    return throwError(() => errorInfo);
  }

  /**
   * Registra un nuevo error
   */
  registerStrategy(strategy: ErrorHandlingStrategy): void {
    this.strategies.push(strategy);
  }

  private initializeStrategies(): void {
    // Errores 401 (No autorizado)
    this.strategies.push({
      canHandle: (error) => error.status === 401,
      handle: (error) => ({
        code: 'UNAUTHORIZED',
        message: 'Token expirado o credenciales inválidas',
        details: error.error,
        userMessage: this.translate.instant('errors.unauthorized'),
        shouldShowToUser: false, // Se maneja automáticamente con refresh token
        requiresAuth: true,
      }),
    });

    // Errores 403 (Prohibido)
    this.strategies.push({
      canHandle: (error) => error.status === 403,
      handle: (error) => ({
        code: 'FORBIDDEN',
        message: 'Acceso denegado - permisos insuficientes',
        details: error.error,
        userMessage: this.translate.instant('errors.forbidden'),
        shouldShowToUser: true,
        requiresAuth: false,
      }),
    });

    // Errores 404 (No encontrado)
    this.strategies.push({
      canHandle: (error) => error.status === 404,
      handle: (error) => ({
        code: 'NOT_FOUND',
        message: 'Recurso no encontrado',
        details: error.error,
        userMessage: this.translate.instant('errors.notFound'),
        shouldShowToUser: true,
        requiresAuth: false,
      }),
    });

    // Errores 422 (Entidad no procesable - errores de validación)
    this.strategies.push({
      canHandle: (error) => error.status === 422,
      handle: (error) => ({
        code: 'VALIDATION_ERROR',
        message: 'Error de validación en los datos enviados',
        details: error.error,
        userMessage: this.extractValidationMessage(error),
        shouldShowToUser: true,
        requiresAuth: false,
      }),
    });

    // Errores 500 (Error interno del servidor)
    this.strategies.push({
      canHandle: (error) => error.status >= 500 && error.status < 600,
      handle: (error) => ({
        code: 'SERVER_ERROR',
        message: 'Error interno del servidor',
        details: error.error,
        userMessage: this.translate.instant('errors.serverError'),
        shouldShowToUser: true,
        requiresAuth: false,
        requiresRetry: true,
      }),
    });

    // Errores de red/conectividad
    this.strategies.push({
      canHandle: (error) => error.status === 0 || !navigator.onLine,
      handle: (error) => ({
        code: 'NETWORK_ERROR',
        message: 'Error de conectividad',
        details: error.error,
        userMessage: this.translate.instant('errors.networkError'),
        shouldShowToUser: true,
        requiresAuth: false,
        requiresRetry: true,
      }),
    });

    // Error para timeout
    this.strategies.push({
      canHandle: (error: unknown): boolean =>
        error instanceof TimeoutError ||
        (error instanceof HttpErrorResponse && error.status === 408),

      handle: (error: unknown): ErrorInfo => {
        const isHttp = error instanceof HttpErrorResponse;

        return {
          code: 'TIMEOUT',
          message: 'Tiempo de espera agotado',
          details: isHttp ? error.error : error,
          userMessage: this.translate.instant('errors.timeout'),
          shouldShowToUser: true,
          requiresAuth: false,
          requiresRetry: true,
        };
      },
    });

    // Errores 400 (Bad Request)
    this.strategies.push({
      canHandle: (error) => error.status === 400,
      handle: (error) => ({
        code: 'BAD_REQUEST',
        message: 'Solicitud incorrecta',
        details: error.error,
        userMessage: this.extractBadRequestMessage(error),
        shouldShowToUser: true,
        requiresAuth: false,
      }),
    });
  }

  private createGenericErrorInfo(error: HttpErrorResponse): ErrorInfo {
    return {
      code: 'UNKNOWN_ERROR',
      message: `Error HTTP ${error.status}: ${error.statusText}`,
      details: error.error,
      userMessage: this.translate.instant('errors.generic'),
      shouldShowToUser: true,
      requiresAuth: false,
    };
  }

  private extractValidationMessage(error: HttpErrorResponse): string {
    // Intenta extraer mensajes de validación específicos del backend
    if (error.error && typeof error.error === 'object') {
      // Para API con errores en formato {field: ['mensaje1', 'mensaje2'] }
      if (error.error.errors) {
        const firstError = Object.values(error.error.errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return firstError[0] as string;
        }
      }

      // Para API que devuelven {message: 'mensaje' }
      if (error.error.message) {
        return error.error.message;
      }

      // Para API que devuelven {detail: 'mensaje'}
      if (error.error.detail) {
        return error.error.detail;
      }
    }

    return this.translate.instant('errors.validationGeneric');
  }

  private extractBadRequestMessage(error: HttpErrorResponse): string {
    if (error.error && typeof error.error === 'object') {
      const possibleFields = ['error', 'message', 'detail', 'msg'];

      for (const field of possibleFields) {
        if (error.error[field]) {
          return error.error[field];
        }
      }

      if (error.error.non_field_errors && Array.isArray(error.error.non_field_errors)) {
        return error.error.non_field_errors[0];
      }

      const errorKeys = Object.keys(error.error);
      for (const key of errorKeys) {
        if (Array.isArray(error.error[key]) && error.error[key].length > 0) {
          return error.error[key][0];
        }
      }
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    const translatedMessage = this.translate.instant('errors.badRequest');
    return translatedMessage !== 'errors.badRequest'
      ? translatedMessage
      : this.translate.instant('errors.badRequestFallback');
  }
}
